import * as React from 'react';
import type { Person } from '@/store/people/types';
import { TagItem } from '@/components/common/tag';
import { useTags } from '@/store/tags/hooks';
import { useApiWithAuth } from '@/hooks/api';
import { usePeopleStore } from '@/store/people';
import { updatePerson } from '@/store/people/actions/people';
import {
  Button,
  Autocomplete,
  AutocompleteItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@heroui/react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { PersonItem } from '@/components/common/output/person';
import { Debug } from '@/components/common/output/debug';

interface TagsProps {
  person: Person;
}

export interface TagsHandle {
  handleUpdate: (onUpdated: (person: Person) => void) => Promise<void>;
  updating: boolean;
}

const Tags = React.forwardRef<TagsHandle, TagsProps>(({ person }, ref) => {
  const { lookup, list: allTags } = useTags();
  const { api, auth } = useApiWithAuth();
  const store = usePeopleStore();
  const tagIds = React.useMemo(
    () => Object.keys(person.data?.tags || {}),
    [person.data?.tags],
  );
  const [localTags, setLocalTags] = React.useState(tagIds);
  const [updating, setUpdating] = React.useState(false);

  React.useEffect(() => {
    setLocalTags(tagIds);
  }, [person.id, tagIds]);

  const handleDelete = async (tagId: string) => {
    setLocalTags(localTags.filter((id) => id !== tagId)); // Optimistic update
  };

  const handleAdd = (key: React.Key | null) => {
    if (!key) {
      return;
    }

    const tagId = String(key);
    setLocalTags([...localTags, tagId]); // Optimistic update
    setInputValue('');
  };

  const [inputValue, setInputValue] = React.useState('');
  const filteredTags = allTags.filter(
    (tag) => tag.id.includes(inputValue) && !localTags.includes(tag.id),
  );

  const handleUpdate = React.useCallback(
    async (onUpdated: (person: Person) => void) => {
      setUpdating(true);
      // Build new tags object
      const tagsData: Record<string, { created?: string; count?: number }> = {};
      for (const tagId of localTags) {
        if (person.data?.tags?.[tagId]) {
          tagsData[tagId] = person.data.tags[tagId];
        } else {
          tagsData[tagId] = { created: '' };
        }
      }

      await updatePerson(
        { store, onUpdated },
        {
          api,
          auth,
          item: {
            id: person.id,
            data: {
              ...person.data,
              tags: tagsData,
            },
          },
        },
      );
      setUpdating(false);
    },
    [localTags, person.data, person.id, store, api, auth],
  );

  React.useImperativeHandle(ref, () => ({ handleUpdate, updating }), [
    updating,
    handleUpdate,
  ]);

  return (
    <div className='flex h-full flex-col gap-2'>
      <PersonItem item={person} className='mb-2' phone={false} email={false} />
      <div className='max-h-48 min-h-[48px] flex-1 overflow-y-auto'>
        <div className='flex flex-col gap-2'>
          {localTags.map((tagId) => {
            const tag = lookup[tagId];
            if (!tag) {
              return null;
            }

            return (
              <div key={tag.id} className='flex w-full items-center gap-2'>
                <div className='flex-1'>
                  <TagItem tag={tag} />
                </div>
                <Button
                  isIconOnly
                  size='sm'
                  variant='light'
                  onPress={() => handleDelete(tag.id)}
                >
                  <Icon
                    icon='mdi:delete'
                    width={20}
                    height={20}
                    className='text-default-500'
                  />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
      {allTags.length > 0 && (
        <div className='mt-2'>
          <Autocomplete
            key={localTags.join(',') + inputValue}
            aria-label='Add tag'
            placeholder={
              filteredTags.length === 0 ? 'No more tags to add' : 'Add tag...'
            }
            inputValue={inputValue}
            onInputChange={setInputValue}
            onSelectionChange={handleAdd}
            items={filteredTags}
            readOnly={filteredTags.length === 0}
            disabled={filteredTags.length === 0}
          >
            {(item) => (
              <AutocompleteItem key={item.id} textValue={item.data.label}>
                <TagItem tag={item} />
              </AutocompleteItem>
            )}
          </Autocomplete>
        </div>
      )}
      <Debug value={{ id: person.id, tags: person.data?.tags }} />
    </div>
  );
});

const TagsModal: React.FC<{
  open: boolean;
  setOpen: (value: boolean) => void;
  currentPerson: Person | null;
  onUpdated: (person: Person) => void;
}> = ({ open, setOpen, currentPerson, onUpdated }) => {
  const tagsRef = React.useRef<TagsHandle>(null);
  const [updating, setUpdating] = React.useState(false);

  const handleUpdate = async () => {
    if (tagsRef.current) {
      setUpdating(true);
      await tagsRef.current.handleUpdate(onUpdated);
      setUpdating(false);
      setOpen(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onOpenChange={setOpen}
      placement='center'
      scrollBehavior='inside'
    >
      <ModalContent>
        <ModalHeader>Edit Tags</ModalHeader>
        <ModalBody className='max-h-[300px] overflow-y-auto'>
          {currentPerson && <Tags person={currentPerson} ref={tagsRef} />}
        </ModalBody>
        <ModalFooter>
          <Button variant='light' onPress={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            color='primary'
            variant='solid'
            onPress={handleUpdate}
            isLoading={updating}
          >
            Update
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TagsModal;
